import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { useTenantPath } from "@/lib/tenantPath";
import { CalendarDays, Users, CheckCircle2, AlertTriangle, TrendingUp, Send, ThumbsUp, ThumbsDown, MailQuestion } from "lucide-react";

const Metric = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-col">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{label}</span>
      <Icon className={`w-4 h-4 ${accent || "text-zinc-400"}`} strokeWidth={2} />
    </div>
    <div className="text-3xl font-bold tracking-tight text-zinc-950 mt-3 font-display">{value}</div>
  </div>
);

export default function Dashboard() {
  const { t } = useTranslation("dashboard");
  const tPath = useTenantPath();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-zinc-500" data-testid="dashboard-loading">{t("loading")}</div>;
  if (!data) return null;

  const { metrics, critical_events, active_events } = data;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
        <p className="text-sm text-zinc-500 mt-1">{t("subtitle")}</p>
      </div>

      <OnboardingChecklist />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Metric icon={CalendarDays} label={t("metrics.activeEvents")} value={metrics.active_events} testid="metric-active-events" />
        <Metric icon={AlertTriangle} label={t("metrics.openEvents")} value={metrics.open_slots ?? metrics.open_events} accent="text-amber-500" testid="metric-open-events" />
        <Metric icon={CheckCircle2} label={t("metrics.confirmedToday")} value={metrics.confirmed_today} accent="text-emerald-600" testid="metric-confirmed-today" />
        <Metric icon={Users} label={t("metrics.confirmedWeek")} value={metrics.confirmed_week} accent="text-emerald-600" testid="metric-confirmed-week" />
        <Metric icon={TrendingUp} label={t("metrics.fillRate")} value={`${metrics.fill_rate}%`} testid="metric-fill-rate" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric icon={Send} label={t("metrics.sent")} value={metrics.total_requests_sent} testid="metric-sent" />
        <Metric icon={ThumbsUp} label={t("metrics.accepted")} value={metrics.accepted} accent="text-emerald-600" testid="metric-accepted" />
        <Metric icon={ThumbsDown} label={t("metrics.refused")} value={metrics.refused} accent="text-red-600" testid="metric-refused" />
        <Metric icon={MailQuestion} label={t("metrics.noResponse")} value={metrics.no_response} accent="text-amber-500" testid="metric-no-response" />
      </div>

      {critical_events.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-amber-100 bg-amber-50/40 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-900">{t("critical.title")}</span>
            <span className="text-xs text-amber-700">{t("critical.subtitle")}</span>
          </div>
          <div className="divide-y divide-zinc-100">
            {critical_events.map((e) => {
              const available = e.shifts_enabled ? 0 : (e.available_ids || []).filter((p) => !(e.confirmed_ids || []).includes(p)).length;
              return (
                <Link key={e.id} to={tPath(`/eventos/${e.id}`)} data-testid={`critical-event-${e.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-zinc-50 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-900 truncate">{e.name}</div>
                    <div className="text-xs text-zinc-500">{e.date} · {e.location}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-zinc-700">
                      <span className="font-semibold text-zinc-950">{e.confirmed_effective ?? e.confirmed_ids.length}/{e.slots_effective ?? e.slots_needed}</span>
                      <span className="text-zinc-400"> {t("critical.confirmedShort")}</span>
                      {available > 0 && <span className="ml-1 text-emerald-700"> · +{available} {t("critical.availableShort")}</span>}
                    </span>
                    <StateBadge status={e.status} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xl font-display font-bold text-zinc-950 mb-3">{t("upcoming.title")}</h2>
        <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100">
          {active_events.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-zinc-500">{t("upcoming.empty")}</div>
          )}
          {active_events.map((e) => {
            const available = e.shifts_enabled ? 0 : (e.available_ids || []).filter((p) => !(e.confirmed_ids || []).includes(p)).length;
            return (
              <Link key={e.id} to={tPath(`/eventos/${e.id}`)} data-testid={`active-event-${e.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-zinc-50 transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">{e.name}</div>
                  <div className="text-xs text-zinc-500">{e.date} · {e.location} · {e.slots_effective ?? e.slots_needed} {t("upcoming.slotsLabel")}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-zinc-700">
                    <span className="font-semibold text-zinc-950">{e.confirmed_effective ?? e.confirmed_ids.length}/{e.slots_effective ?? e.slots_needed}</span>
                    <span className="text-zinc-400"> {t("upcoming.confirmed")}</span>
                    {available > 0 && <span className="ml-1.5 text-emerald-700">+{available} {t("upcoming.available")}</span>}
                  </span>
                  <StateBadge status={e.status} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
